import React, { forwardRef, ImgHTMLAttributes, useLayoutEffect, useState } from 'react';
import { ImageProps } from 'vev';
import { useGlobalStateRef, useImage, useIsIndexPage } from '../../core/hooks';
import { View } from '../../manager';
import { isString } from '../../utils';

type ImageSize = [number, number];

function clean(str: string): string {
  const removeLineBreaks = (str = '') => str.replace(/\n/g, '');
  return removeLineBreaks(str || '');
}

function getSize([deviceSize, resSize]: ImageSize) {
  return `(max-width: ${deviceSize}px) ${resSize}px`;
}

function sortByDevice(s1: ImageSize, s2: ImageSize) {
  return s1[0] - s2[0];
}

const Image = forwardRef(({ src, sizes: modeSizes, style, className, alt }: ImageProps, ref) => {
  const img = useImage(src ? (src as any).key || src : '');
  const isIndexPage = useIsIndexPage();
  const [stateRef] = useGlobalStateRef();
  const [loading, setLoading] = useState<'lazy' | undefined>('lazy');

  useLayoutEffect(() => {
    const isWebKit = /safari/i.test(navigator.userAgent);
    // Do not use lazy loading on Safari (due to animation issues)
    if (isWebKit) setLoading(undefined);
  }, []);

  function replaceDotDotSlash(str: string): string {
    return str.replace(new RegExp('\\.\\.\\/', 'g'), '/');
  }

  let srcSet: any, sizes: any;
  if (img) {
    if (modeSizes) {
      modeSizes.sort(sortByDevice);
      sizes = '';
      const len = modeSizes.length - 1;
      for (let i = 0; i < len; i++) sizes += getSize(modeSizes[i]) + ' ';
      sizes += modeSizes[len][1] + 'px';
    }

    srcSet = img.srcset.map((imgSize) => `${imgSize[0]} ${imgSize[1]}w`).join(',');
    src = img.src || (img as any).url;

    if (isIndexPage) {
      srcSet = replaceDotDotSlash(srcSet);
    }

    if (!src && img.srcset[img.srcset.length - 1]) src = img.srcset[img.srcset.length - 1][0];
    if (!alt) alt = img.meta && img.meta.description;
  } else {
    src = isString(src) ? src : src?.key;
  }

  if (isString(src) && /^\/\//.test(src)) src = 'https:' + src;

  const replacedSrc = isIndexPage && isString(src) ? replaceDotDotSlash(src) : src;

  const imgProps: ImgHTMLAttributes<HTMLImageElement> = {
    style,
    loading,
    className,
    src: isString(replacedSrc) ? replacedSrc : '',
    srcSet,
    sizes,
    alt: alt ? clean(alt) : undefined,
  };

  // const loadStrategy = stateRef.current?.settings.load;
  // if (loadStrategy === 'lazy' || (View.isIOS && loadStrategy === 'lazy-ios')) {
  //   // Allow hook inside of if since loading strategy is either always on or always off
  //   const visible = useVisible(ref as any, {
  //     offsetBottom: -window.innerHeight,
  //     offsetTop: -window.innerHeight,
  //   });
  //   const src = imgProps.src as string;
  //   if (!visible && src?.includes('cdn-cgi/image/')) {
  //     // Loading a tiny image to avoid layout shifts
  //     return (
  //       <img
  //         {...imgProps}
  //         ref={ref as any}
  //         src={src.replace(/cdn-cgi\/image\/[^/]+/, 'cdn-cgi/image/w=50,blur=10')}
  //         srcSet={undefined}
  //         sizes={undefined}
  //       />
  //     );
  //   }
  // }
  return <img ref={ref as any} {...imgProps} />;
});

Image.displayName = 'Image';
export default Image;
